// Mobile bottom nav (Q11 — DESIGN-DECISIONS-2026-05-01).
// Visible <768px only. Top 4 destinations + "More" tab that opens the drawer.
// Above 768px: hidden, sidebar takes over.

import type { JSX, ReactNode } from "react";
import { NavLink } from "react-router-dom";

interface Props {
  onOpenMore: () => void;
}

interface TabIconProps {
  children: ReactNode;
}

function TabIcon({ children }: TabIconProps): JSX.Element {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-5 w-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      {children}
    </svg>
  );
}

const TAB_BASE = "flex-1 flex flex-col items-center justify-center gap-0.5 py-2";
const ACTIVE = "text-navy-700";
const INACTIVE = "text-slate-500";

function NavTab({
  to,
  label,
  icon,
}: {
  to: string;
  label: string;
  icon: ReactNode;
}): JSX.Element {
  return (
    <NavLink
      to={to}
      className={({ isActive }) => `${TAB_BASE} ${isActive ? ACTIVE : INACTIVE}`}
    >
      {({ isActive }) => (
        <>
          <span
            className={`flex items-center justify-center w-9 h-7 rounded-md ${
              isActive ? "bg-navy-50" : ""
            }`}
          >
            {icon}
          </span>
          <span className="text-[11px] font-medium leading-none">{label}</span>
        </>
      )}
    </NavLink>
  );
}

export function BottomNav({ onOpenMore }: Props): JSX.Element {
  return (
    <nav
      role="navigation"
      aria-label="Primary mobile navigation"
      className="md:hidden fixed bottom-0 inset-x-0 h-16 bg-white border-t border-slate-200 shadow-[0_-4px_12px_-4px_rgba(0,0,0,0.08)] flex z-20"
    >
      <NavTab
        to="/dashboard"
        label="Dashboard"
        icon={
          <TabIcon>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7m-9 2v8m4-8v8m5-8l2 2M5 10v10a1 1 0 001 1h3m10-11l-2-2m2 2v10a1 1 0 01-1 1h-3m-6 0h6" />
          </TabIcon>
        }
      />
      <NavTab
        to="/sr"
        label="SRs"
        icon={
          <TabIcon>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </TabIcon>
        }
      />
      <NavTab
        to="/patients"
        label="Patients"
        icon={
          <TabIcon>
            <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </TabIcon>
        }
      />
      <NavTab
        to="/carers"
        label="Carers"
        icon={
          <TabIcon>
            <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </TabIcon>
        }
      />
      <button
        type="button"
        onClick={onOpenMore}
        className={`${TAB_BASE} ${INACTIVE}`}
        aria-label="Open more navigation"
      >
        <span className="flex items-center justify-center w-9 h-7 rounded-md">
          <TabIcon>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
          </TabIcon>
        </span>
        <span className="text-[11px] font-medium leading-none">More</span>
      </button>
    </nav>
  );
}
